import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import pb from '../lib/pocketbaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(pb.authStore.model);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if there's a valid session on mount
    if (pb.authStore.isValid) {
      setUser(pb.authStore.model);
    } else {
      setUser(null);
    }
    setLoading(false);

    // Listen for auth state changes
    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model);
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const authData = await pb.collection('users').authWithPassword(email, password);
      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signUp = useCallback(async (email, password) => {
    try {
      // Create the user first
      await pb.collection('users').create({
        email,
        password,
        passwordConfirm: password,
      });
      // Then authenticate
      const authData = await pb.collection('users').authWithPassword(email, password);
      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      pb.authStore.clear();
      return { error: null };
    } catch (error) {
      return { error };
    }
  }, []);

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
